import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/solutions", label: "Solutions" },
  { to: "/apps", label: "Apps" },
  { to: "/ai-suite", label: "AI Suite" },
  { to: "/pricing", label: "Pricing" },
  { to: "/industries", label: "Industries" },
  { to: "/integrations", label: "Integrations" },
  { to: "/security", label: "Security" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  // Auto-close menu when pathname changes; we read it but use closure trick
  const closeMenu = () => setOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 transition-all ${scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-white/0 border-b border-transparent"
        }`}
    >
      <div className="container-x flex items-center justify-between h-[72px] md:h-[88px]">
        <Link
          to="/"
          className="flex items-center gap-[10px]"
          data-testid="nav-logo"
          onClick={(e) => {
            if (pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <Logo className="h-11 xl:h-[52px]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 xl:gap-[6px]">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `px-2.5 xl:px-[15px] py-2 xl:py-[9px] rounded-full text-[13px] xl:text-[15px] font-bold transition-colors ${isActive
                  ? "text-[color:var(--brand)] bg-[color:var(--brand-soft)]"
                  : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5 xl:gap-4">
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="btn-primary text-[13px] xl:text-[15px] font-bold py-2 px-4 xl:py-3 xl:px-6"
          >
            Get in Touch
            <ArrowUpRight className="w-4 h-4 xl:w-[18px] xl:h-[18px]" />
          </Link>
          {/*
          <Link to="/contact" className="text-[13px] xl:text-[15px] font-bold text-slate-700 hover:text-slate-950" data-testid="nav-contact">
            Contact
          </Link>
          <Link
            to="/request-demo"
            data-testid="nav-request-demo"
            className="btn-primary text-[13px] xl:text-[15px] font-bold py-2 px-4 xl:py-3 xl:px-6"
          >
            Request Demo
            <ArrowUpRight className="w-4 h-4 xl:w-[18px] xl:h-[18px]" />
          </Link>
          */}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 rounded-full border border-slate-200"
          onClick={() => setOpen(v => !v)}
          aria-label="menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-xl text-sm font-bold ${isActive ? "bg-[color:var(--brand-soft)] text-[color:var(--brand)]" : "text-slate-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" onClick={closeMenu} className="btn-primary font-bold justify-center mt-4 py-3" data-testid="mobile-nav-contact">
              Get in Touch <ArrowUpRight className="w-5 h-5" />
            </Link>
            {/*
            <Link to="/contact" onClick={closeMenu} className="px-3 py-2.5 text-sm font-bold">Contact</Link>
            <Link to="/request-demo" onClick={closeMenu} className="btn-primary font-bold justify-center mt-2" data-testid="mobile-request-demo">
              Request Demo <ArrowUpRight className="w-4 h-4" />
            </Link>
            */}
          </div>
        </div>
      )}
    </header>
  );
}
